/**
 * TanStack Query hooks for the notifications feature. Mirrors the mobile
 * hooks (apps/mobile/src/features/notifications/api/use-notifications-query.ts)
 * so both inboxes behave identically.
 */
import { useEffect } from 'react'
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import {
  fetchNotifications,
  fetchUnreadCount,
  fetchEventCatalog,
  fetchMutedEventTypes,
  markRead,
  markAllRead,
  dismissNotification,
  muteEventType,
  unmuteEventType,
  type FetchNotificationsOpts,
} from '../api/notifications-api'

export function notificationsNextPageParam(pageSize: number) {
  return (lastPage: unknown[], allPages: unknown[][]): number | undefined =>
    // A short page means the server had nothing more to give; an exactly
    // page-sized final page costs one extra request rather than truncating.
    lastPage.length < pageSize ? undefined : allPages.length
}

export function useNotificationsInfiniteQuery(
  projectId: string | undefined,
  opts: Omit<FetchNotificationsOpts, 'page'> = {},
) {
  const userId = useAuthStore((s) => s.user?.id)
  const pageSize = opts.pageSize ?? 30
  return useInfiniteQuery({
    queryKey: ['notifications', projectId, opts.unreadOnly ?? false, opts.eventType ?? null, opts.includeDismissed ?? false, pageSize],
    queryFn: ({ pageParam }) =>
      fetchNotifications(userId!, projectId!, { ...opts, page: pageParam, pageSize }),
    initialPageParam: 0,
    getNextPageParam: notificationsNextPageParam(pageSize),
    enabled: !!userId && !!projectId,
  })
}

export function useUnreadCountQuery(projectId: string | undefined) {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['notifications-unread', projectId],
    queryFn: () => fetchUnreadCount(userId!, projectId!),
    enabled: !!userId && !!projectId,
    staleTime: 0,
  })
}

function useInvalidateNotifications(projectId: string | undefined) {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['notifications', projectId] })
    qc.invalidateQueries({ queryKey: ['notifications-unread', projectId] })
  }
}

export function useMarkReadMutation(projectId: string | undefined) {
  const invalidate = useInvalidateNotifications(projectId)
  return useMutation({ mutationFn: (id: string) => markRead(id), onSuccess: invalidate })
}

export function useMarkAllReadMutation(projectId: string | undefined) {
  const invalidate = useInvalidateNotifications(projectId)
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    mutationFn: () => markAllRead(userId!, projectId!),
    onSuccess: invalidate,
  })
}

export function useDismissMutation(projectId: string | undefined) {
  const invalidate = useInvalidateNotifications(projectId)
  return useMutation({
    mutationFn: (id: string) => dismissNotification(id),
    onSuccess: invalidate,
  })
}

/**
 * The DB event-type catalog — the dynamic source for labels of event types
 * this client build does not know, and for the settings mute list.
 */
export function useEventCatalogQuery() {
  return useQuery({
    queryKey: ['notification-event-types'],
    queryFn: fetchEventCatalog,
    staleTime: 60 * 60 * 1000,
  })
}

export function useMutedEventTypesQuery(projectId: string | undefined) {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['notification-muted', projectId],
    queryFn: () => fetchMutedEventTypes(userId!, projectId!),
    enabled: !!userId && !!projectId,
  })
}

export function useMuteToggleMutation(projectId: string | undefined) {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    mutationFn: ({ eventType, mute }: { eventType: string; mute: boolean }) =>
      mute
        ? muteEventType(userId!, projectId!, eventType)
        : unmuteEventType(userId!, projectId!, eventType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notification-muted', projectId] })
    },
  })
}

/**
 * Realtime delivery: invalidate the feed + badge whenever a row for this user
 * changes. Identical strategy to the mobile hook — invalidate-on-change, no
 * client-side row merging.
 */
export function useNotificationsRealtime(projectId: string | undefined) {
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)

  useEffect(() => {
    if (!userId || !projectId) return
    // Unique channel name per effect run: supabase-js caches channels by name,
    // and a reused name can hand back an already-subscribed channel (throws
    // under React dev double-mount).
    const channel = supabase
      .channel(`notifications:${userId}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `recipient_user_id=eq.${userId}` },
        () => {
          qc.invalidateQueries({ queryKey: ['notifications', projectId] })
          qc.invalidateQueries({ queryKey: ['notifications-unread', projectId] })
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, projectId, qc])
}
